import { useEffect, useRef, type KeyboardEvent } from 'react'
import { Button, Input, Select } from '@indipia/ui-core'
import {
  ORDER_LIMITS, ORDER_MESSAGE, emptyDraftItem, unitOptions, type DraftItemErrors, type OrderDraftItem,
} from '../../constants/materialOrder.logic'

// 발주 품목 줄 편집 — 품목명·규격·수량·단위(품목명·수량·단위 필수). 최대 30줄.
// 입력값은 문자열로 들고 있고(소수 2자리 검증은 validateDraft), 줄 오류는 해당 칸 error 로 표시한다.
// 단위는 7종 선택(기존 값이 7종 밖이면 그 값을 선택지에 추가해 보존). Enter 는 칸 순서(품목명→규격→수량→단위→다음 줄)로 이동하고,
// 마지막 줄 단위에서 Enter 는 새 줄을 추가한다(30줄 상한 안에서, 빈 줄이면 추가하지 않음).

const CELL_COUNT = 4
const LAST_CELL = CELL_COUNT - 1

function focusCell(root: HTMLElement | null, row: number, col: number) {
  root?.querySelector<HTMLElement>(`[data-cell="${row}-${col}"]`)?.focus()
}

function isBlankLine(i: OrderDraftItem): boolean {
  return !i.itemName.trim() && !i.itemSpec.trim() && !i.itemQty.trim() && !i.itemUnit.trim()
}

interface Props {
  items: OrderDraftItem[]
  /** 줄 번호(0부터) → 칸별 오류 */
  lineErrors?: Record<number, DraftItemErrors>
  /** 품목 전체에 대한 오류(예: 30줄 초과) */
  totalError?: string
  disabled?: boolean
  onChange: (items: OrderDraftItem[]) => void
}

export function OrderItemsEditor({ items, lineErrors, totalError, disabled = false, onChange }: Props) {
  const lines = items.length > 0 ? items : [emptyDraftItem()]
  const patchLine = (i: number, patch: Partial<OrderDraftItem>) => {
    onChange(lines.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
  }
  const removeLine = (i: number) => {
    const next = lines.filter((_, idx) => idx !== i)
    onChange(next.length > 0 ? next : [emptyDraftItem()])
  }
  const atMax = lines.length >= ORDER_LIMITS.itemsMax

  const rootRef = useRef<HTMLFieldSetElement>(null)
  const pendingFocusRow = useRef<number | null>(null)
  // 새 줄이 그려진 뒤 그 줄 품목명으로 포커스
  useEffect(() => {
    if (pendingFocusRow.current == null) return
    focusCell(rootRef.current, pendingFocusRow.current, 0)
    pendingFocusRow.current = null
  }, [lines.length])
  const onCellEnter = (e: KeyboardEvent<HTMLElement>, row: number, col: number) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
    e.preventDefault()
    if (col < LAST_CELL) { focusCell(rootRef.current, row, col + 1); return }
    if (row < lines.length - 1) { focusCell(rootRef.current, row + 1, 0); return }
    if (atMax || isBlankLine(lines[row])) return
    pendingFocusRow.current = row + 1
    onChange([...lines, emptyDraftItem()])
  }

  return (
    <fieldset ref={rootRef} className="flex flex-col gap-2" disabled={disabled}>
      <legend className="text-body-sm font-semibold text-text-primary">
        {ORDER_MESSAGE.itemsTitle} <span className="text-text-muted">{ORDER_MESSAGE.required}</span>
      </legend>
      <ul className="flex flex-col gap-2">
        {lines.map((it, i) => {
          const err = lineErrors?.[i]
          return (
            <li key={i} className="grid grid-cols-12 items-start gap-2">
              <Input
                className="col-span-4"
                width="fill"
                aria-label={ORDER_MESSAGE.popup.itemLabel(i + 1, ORDER_MESSAGE.itemName)}
                placeholder={ORDER_MESSAGE.itemName}
                value={it.itemName}
                error={err?.itemName}
                data-cell={`${i}-0`}
                onKeyDown={e => onCellEnter(e, i, 0)}
                onChange={e => patchLine(i, { itemName: e.target.value })}
              />
              <Input
                className="col-span-3"
                width="fill"
                aria-label={ORDER_MESSAGE.popup.itemLabel(i + 1, ORDER_MESSAGE.itemSpec)}
                placeholder={ORDER_MESSAGE.itemSpec}
                value={it.itemSpec}
                error={err?.itemSpec}
                data-cell={`${i}-1`}
                onKeyDown={e => onCellEnter(e, i, 1)}
                onChange={e => patchLine(i, { itemSpec: e.target.value })}
              />
              <Input
                className="col-span-2"
                width="fill"
                inputMode="decimal"
                aria-label={ORDER_MESSAGE.popup.itemLabel(i + 1, ORDER_MESSAGE.itemQty)}
                placeholder={ORDER_MESSAGE.itemQty}
                value={it.itemQty}
                error={err?.itemQty}
                data-cell={`${i}-2`}
                onKeyDown={e => onCellEnter(e, i, 2)}
                onChange={e => patchLine(i, { itemQty: e.target.value })}
              />
              <Select
                className="col-span-2 !max-w-none"
                aria-label={ORDER_MESSAGE.popup.itemLabel(i + 1, ORDER_MESSAGE.itemUnit)}
                value={it.itemUnit}
                error={err?.itemUnit}
                data-cell={`${i}-3`}
                onKeyDown={e => onCellEnter(e, i, 3)}
                onChange={e => patchLine(i, { itemUnit: e.target.value })}
              >
                <option value="">{ORDER_MESSAGE.unitPlaceholder}</option>
                {unitOptions(it.itemUnit).map(u => <option key={u} value={u}>{u}</option>)}
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="col-span-1"
                aria-label={ORDER_MESSAGE.popup.itemRemove(i + 1)}
                disabled={disabled}
                onClick={() => removeLine(i)}
              >×</Button>
            </li>
          )
        })}
      </ul>
      {totalError && <p className="text-caption text-error" role="alert">{totalError}</p>}
      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled || atMax}
          onClick={() => onChange([...lines, emptyDraftItem()])}
        >
          {ORDER_MESSAGE.addItem}
        </Button>
        {atMax && <p className="mt-1 text-caption text-text-muted">{ORDER_MESSAGE.addItemMax}</p>}
      </div>
    </fieldset>
  )
}
